'use client'

import 'scrollyfills'

import { ArrowsOutSimple, CaretLeft, CaretRight } from '@phosphor-icons/react'

import type { KeyboardEventHandler } from 'react'
import { memo, useEffect, useRef, useState } from 'react'

import { cx } from '../../cx'
import { useMediaState } from '../../hooks/use-media-state'
import { uiTestId } from '../../test-ids'
import type { IconButtonProps } from '../button/icon-button'
import { IconButton } from '../button/icon-button'
import { SimpleModal } from '../modal'
import { ImageSliderContainer } from './image-slider-container'
import { ImageSliderItem } from './image-slider-item'
import { ImageSliderLightbox } from './image-slider-lightbox'
import { ImageSliderSwipeDots } from './image-slider-swipe-dots'

export type SliderImage = {
  url: string
  thumbnailUrl?: string | undefined | null
  alt?: string | null | undefined
  copyright?: string | null | undefined
  sizes?: string | null | undefined
}

export type ImageSliderProps = {
  images: SliderImage[]
  hideButtons?: boolean
  disableLightbox?: boolean
  hideNumberIndex?: boolean

  lightboxTitle?: string
  disableCornerRadius?: boolean
  className?: string
  buttonSize?: IconButtonProps['size']
  reduceTransitions?: boolean
}

export const ImageSlider = memo(function ImageSlider({
  images,
  lightboxTitle,
  disableCornerRadius,
  className,
  hideButtons,
  hideNumberIndex,
  disableLightbox,
  buttonSize = 'medium',
  reduceTransitions,
}: ImageSliderProps) {
  const containerRef = useRef<HTMLUListElement>(null)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [lightBoxOpen, setLightBoxOpen] = useState(false)
  const canHover = useMediaState('pointerFine')

  useEffect(() => {
    const container = containerRef.current
    const handler = (event: HTMLElementEventMap['scrollend']) => {
      const target = event.currentTarget
      if (!(target instanceof HTMLUListElement)) return
      setCurrentIndex(Math.round(target.scrollLeft / target.clientWidth))
    }

    container?.addEventListener('scrollend', handler)

    return () => container?.removeEventListener('scrollend', handler)
  }, [])

  const scrollToIndex = (index: number) => {
    containerRef.current?.scrollTo({
      left: index * containerRef.current.clientWidth,
      behavior: 'smooth',
    })
  }

  const handleNav = (direction: 'prev' | 'next') => {
    setCurrentIndex(oldIndex => {
      const newIndex = direction === 'prev' ? oldIndex - 1 : oldIndex + 1

      if (newIndex >= 0 && newIndex < images.length) {
        scrollToIndex(newIndex)
        return newIndex
      }

      return oldIndex
    })
  }

  const keyDownHandler: KeyboardEventHandler<HTMLDivElement> = e => {
    switch (e.key) {
      case 'ArrowRight':
        handleNav('next')
        break
      case 'ArrowLeft':
        handleNav('prev')
        break
      default:
        break
    }
  }

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- TODO: fix accessability */}
      <div
        className={cx('group relative size-full focus:outline-hidden', className)}
        onKeyDown={keyDownHandler}
        onClick={!canHover && !disableLightbox ? () => setLightBoxOpen(true) : undefined}
      >
        <ImageSliderContainer ref={containerRef} corners={disableCornerRadius ? 'none' : 'rounded'}>
          {images.map((image, index) => (
            <ImageSliderItem
              key={index}
              image={image}
              imageSizes={image.sizes ?? '(max-width: 768px) 80vw,1000px'}
              reduceTransitions={reduceTransitions}
            />
          ))}
        </ImageSliderContainer>

        {!hideNumberIndex && (
          <div className="absolute top-4 right-4 inline-flex items-center justify-center rounded-md bg-black/50 px-2 py-0.5">
            <div className="text-center font-base-copy text-white">
              {currentIndex + 1}/{images.length}
            </div>
          </div>
        )}

        {images.length > 1 && (
          <div className="pointer-events-none absolute bottom-4 flex w-full justify-center space-x-2">
            <ImageSliderSwipeDots currentIndex={currentIndex} length={images.length} />
          </div>
        )}

        {!hideButtons && canHover && currentIndex > 0 && (
          <div className="absolute top-0 left-4 flex h-full items-center opacity-0 transition-opacity group-hover:opacity-100">
            <IconButton
              data-testid={uiTestId('ImageSlider.prevButton')}
              onClick={e => {
                e.preventDefault()
                handleNav('prev')
              }}
              Icon={CaretLeft}
              variant="faded"
              size={buttonSize}
              aria-label="Vorheriges Bild"
            />
          </div>
        )}

        {!hideButtons && canHover && currentIndex < images.length - 1 && (
          <div className="absolute top-0 right-4 flex h-full items-center justify-center opacity-0 transition-opacity group-hover:opacity-100">
            <IconButton
              data-testid={uiTestId('ImageSlider.nextButton')}
              onClick={e => {
                e.preventDefault()
                handleNav('next')
              }}
              Icon={CaretRight}
              variant="faded"
              size={buttonSize}
              aria-label="Nächstes Bild"
            />
          </div>
        )}

        {!disableLightbox && canHover && (
          <div className="absolute right-4 bottom-4 opacity-0 transition-opacity group-hover:opacity-100">
            <IconButton
              data-testid={uiTestId('ImageSlider.lightboxButton')}
              onClick={e => {
                e.preventDefault()
                setLightBoxOpen(true)
              }}
              Icon={ArrowsOutSimple}
              variant="faded"
              size={buttonSize}
              aria-label="Bildergalerie öffnen"
            />
          </div>
        )}
      </div>

      {!disableLightbox && (
        <SimpleModal
          isOpen={lightBoxOpen}
          onClose={() => setLightBoxOpen(false)}
          title={lightboxTitle}
          height="full"
          size="full"
        >
          <ImageSliderLightbox images={images} initialIndex={currentIndex} />
        </SimpleModal>
      )}
    </>
  )
})
